/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  AppBar, Avatar, Box, Button, Chip, Divider, IconButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Toolbar, Tooltip, Typography,
} from '@mui/material'
import BrandName from './BrandName'
import MenuIcon from '@mui/icons-material/Menu'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import CheckIcon from '@mui/icons-material/Check'
import LogoutIcon from '@mui/icons-material/Logout'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { logout, signOut } from '../../store/slices/authSlice'
import { authAPI } from '../../api/auth'
import ChangePasswordDialog from './ChangePasswordDialog'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined'
import { selectTenant } from '../../store/slices/tenantSlice'
import { useMyTenants, useProfile } from '../../hooks/useCrmQueries'
import ProfileDialog from './ProfileDialog'
import { useColorMode } from '../../theme/ColorModeProvider'
import NotificationBell from './NotificationBell'

const Navbar = ({ onMenuClick }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const user = useSelector((state) => state.auth.user)
  const tenant = useSelector((state) => state.tenant.current)
  const { data: tenants = [] } = useMyTenants()
  const { mode, toggle } = useColorMode()
  const [tenantAnchor, setTenantAnchor] = useState(null)
  const [userAnchor, setUserAnchor] = useState(null)
  const { data: profile } = useProfile()
  const displayName = profile?.displayName
  const [profileDialog, setProfileDialog] = useState(null) // null | 'edit' | 'prompt'
  const [changingPassword, setChangingPassword] = useState(false)

  // ask once per browser for a name, so teammates don't only see an email
  useEffect(() => {
    if (!profile || profile.displayName) return
    try {
      if (localStorage.getItem('namePromptShown')) return
      localStorage.setItem('namePromptShown', '1')
    } catch {
      return
    }
    setProfileDialog('prompt')
  }, [profile])

  const switchTenant = (next) => {
    setTenantAnchor(null)
    if (next.tenantId === tenant?.tenantId) return
    dispatch(selectTenant(next))
    queryClient.removeQueries()
    navigate('/')
  }

  const handleLogout = async () => {
    setUserAnchor(null)
    await dispatch(signOut())
    queryClient.clear()
    navigate('/login')
  }

  const handleLogoutEverywhere = async () => {
    setUserAnchor(null)
    try {
      await authAPI.logoutAll()
    } finally {
      dispatch(logout())
      queryClient.clear()
      navigate('/login')
    }
  }

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar variant="dense" sx={{ gap: 1 }}>
        <IconButton onClick={onMenuClick} sx={{ display: { md: 'none' } }} aria-label="Open navigation">
          <MenuIcon />
        </IconButton>
        {/* phones: the workspace switcher needs the room more than the logo */}
        <BrandName sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }} />

        <Button
          color="inherit"
          onClick={(e) => setTenantAnchor(e.currentTarget)}
          endIcon={<UnfoldMoreIcon fontSize="small" />}
          sx={{ maxWidth: { xs: 'calc(100vw - 190px)', sm: 260 }, minWidth: 0, border: 1, borderColor: 'divider', px: 1.5 }}
        >
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {tenant?.name || 'Select tenant'}
          </Typography>
        </Button>
        <Menu anchorEl={tenantAnchor} open={!!tenantAnchor} onClose={() => setTenantAnchor(null)}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
            Switch tenant
          </Typography>
          {tenants.map((t) => (
            <MenuItem key={t.tenantId} onClick={() => switchTenant(t)} disabled={t.status !== 'ACTIVE'} sx={{ minWidth: 240 }}>
              <ListItemIcon>{t.tenantId === tenant?.tenantId && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={t.name} secondary={t.status !== 'ACTIVE' ? t.status.toLowerCase() : t.role === 'TENANT_ADMIN' ? 'Admin' : 'Member'} />
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ flexGrow: 1 }} />

        {tenant && <NotificationBell />}
        <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
          <IconButton onClick={toggle} aria-label="Toggle color mode">
            {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>
        {tenant?.role === 'TENANT_ADMIN' && <Chip label="Admin" color="primary" variant="outlined" sx={{ display: { xs: 'none', sm: 'flex' } }} />}
        <IconButton onClick={(e) => setUserAnchor(e.currentTarget)} aria-label="Account menu">
          <Avatar src={profile?.pictureUrl || undefined} imgProps={{ referrerPolicy: 'no-referrer' }} alt=""
            sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'primary.main' }}>
            {(displayName || user?.email || '?')[0].toUpperCase()}
          </Avatar>
        </IconButton>
        <Menu anchorEl={userAnchor} open={!!userAnchor} onClose={() => setUserAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName || user?.email}</Typography>
            {displayName && <Typography variant="caption" color="text.secondary">{user?.email}</Typography>}
          </Box>
          <Divider />
          <MenuItem onClick={() => { setUserAnchor(null); setProfileDialog('edit') }}>
            <ListItemIcon><BadgeOutlinedIcon fontSize="small" /></ListItemIcon>
            {displayName ? 'Change your name' : 'Set your name'}
          </MenuItem>
          <MenuItem onClick={() => { setUserAnchor(null); setChangingPassword(true) }}>
            <ListItemIcon><KeyOutlinedIcon fontSize="small" /></ListItemIcon>
            Change password
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogoutEverywhere}>
            <ListItemIcon><DevicesOutlinedIcon fontSize="small" /></ListItemIcon>
            Sign out everywhere
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
        {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
        {profileDialog && (
          <ProfileDialog open prompt={profileDialog === 'prompt'} initialName={displayName || ''} onClose={() => setProfileDialog(null)} />
        )}
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
